import type { DataGridCellAddr } from "../types";
import { cellAddrEquals } from "../utils/buildNavigationMatrix";

export type DataGridEditState =
	| { kind: "idle" }
	| { kind: "editing"; rowId: string; colId: string; draft: string; selectOnFocus: boolean };

export type DataGridState = {
	expanded: Set<string>;
	activeCell: DataGridCellAddr | null;
	edit: DataGridEditState;
};

export type DataGridAction =
	| { type: "toggle_row"; rowId: string }
	| { type: "set_expanded"; rowIds: Iterable<string> }
	| { type: "set_active_cell"; cell: DataGridCellAddr }
	| { type: "clear_active_cell" }
	| { type: "start_edit"; rowId: string; colId: string; initial: string; selectOnFocus: boolean }
	| { type: "change_draft"; draft: string }
	| { type: "commit_edit" }
	| { type: "cancel_edit" };

export const initialDataGridState = (defaults: { expanded?: Iterable<string> }): DataGridState => ({
	expanded: new Set(defaults.expanded ?? []),
	activeCell: null,
	edit: { kind: "idle" },
});

export function dataGridReducer(state: DataGridState, action: DataGridAction): DataGridState {
	switch (action.type) {
		case "toggle_row": {
			const next = new Set(state.expanded);
			if (next.has(action.rowId)) next.delete(action.rowId);
			else next.add(action.rowId);
			return { ...state, expanded: next };
		}
		case "set_expanded":
			return { ...state, expanded: new Set(action.rowIds) };
		case "set_active_cell":
			if (state.activeCell && cellAddrEquals(state.activeCell, action.cell)) return state;
			return { ...state, activeCell: action.cell };
		case "clear_active_cell":
			if (state.activeCell === null) return state;
			return { ...state, activeCell: null, edit: { kind: "idle" } };
		case "start_edit":
			return {
				...state,
				activeCell: { rowId: action.rowId, colId: action.colId },
				edit: {
					kind: "editing",
					rowId: action.rowId,
					colId: action.colId,
					draft: action.initial,
					selectOnFocus: action.selectOnFocus,
				},
			};
		case "change_draft":
			if (state.edit.kind !== "editing") return state;
			if (state.edit.draft === action.draft) return state;
			return { ...state, edit: { ...state.edit, draft: action.draft } };
		case "commit_edit":
		case "cancel_edit":
			if (state.edit.kind !== "editing") return state;
			return { ...state, edit: { kind: "idle" } };
	}
}
